import type { AnchorV2 } from "./types";

/**
 * Çapa haritası: iki tarafın konumları arasında dönüşüm (plan B4).
 *
 * <p><b>Neden oranlı eşleme değil.</b> "Sol %30'daysa sağ da %30'a git" sayfa sayıları
 * farklı olduğunda çöker — korpusta alarko çiftinde sol 1 sayfa, sağ 2 sayfa; oranlı eşleme
 * sağdaki her şeyi yarı yarıya kaydırır. Çapa haritası çökmez çünkü karşılıklar ÖLÇÜLMÜŞ:
 * motorun mantıksal düğüm eşleştirmelerinden geliyorlar.</p>
 *
 * <p><b>Çapa yoksa senkron KAPANIR.</b> Yaklaşık bir eşleme uydurmak, kullanıcıyı yanlış
 * yere götürüp doğru gittiğini sandırmak demek. Bu modül boş harita için
 * <see cref="AnchorMap.usable"/> false döner ve arayüz senkronu kapatıp bunu söyler.</p>
 */

/** Belge içinde monoton tek bir konum ölçüsü: sayfa numarası + sayfa içi oran. */
export interface DocumentPosition {
  /** 1-tabanlı sayfa numarası. */
  page: number;
  /** Sayfa içindeki dikey oran [0,1]. */
  offset: number;
}

export interface AnchorMap {
  /** Senkron yapılabilir mi — en az iki çapa gerekiyor (interpolasyon için). */
  usable: boolean;
  /** Kaç çapa kullanılıyor; arayüz bunu kullanıcıya söylüyor. */
  count: number;
  /** Birinci taraftaki konumu ikinci taraftakine çevirir. */
  firstToSecond: (position: DocumentPosition) => DocumentPosition | null;
  /** İkinci taraftaki konumu birinci taraftakine çevirir. */
  secondToFirst: (position: DocumentPosition) => DocumentPosition | null;
}

/** Monoton skaler: sayfa + oran. Sayfa sınırlarını aşan interpolasyonu mümkün kılıyor. */
function scalar(position: DocumentPosition): number {
  return position.page + Math.max(0, Math.min(1, position.offset));
}

function toPosition(value: number): DocumentPosition {
  const page: number = Math.max(1, Math.floor(value));
  return { page, offset: Math.max(0, Math.min(1, value - page)) };
}

interface AnchorPair {
  first: number;
  second: number;
}

/**
 * Çapa dizisinden harita kurar.
 *
 * <p>Yalnızca İKİ TARAFI DA konumlu çapalar kullanılıyor; tek taraflı bir çapa karşılık
 * değildir. Aynı skaler değere düşen çapalar teklenip sıralanıyor: interpolasyonun monoton
 * olması için harita da monoton olmak zorunda, yoksa kaydırma ileri giderken geri
 * zıplayabilir.</p>
 */
export function buildAnchorMap(anchors: AnchorV2[]): AnchorMap {
  const pairs: AnchorPair[] = [];
  for (const anchor of anchors) {
    const first = anchor.first;
    const second = anchor.second;
    if (!first || !second || first.page <= 0 || second.page <= 0) continue;
    pairs.push({
      first: scalar({ page: first.page, offset: first.rect ? first.rect[1] : 0 }),
      second: scalar({ page: second.page, offset: second.rect ? second.rect[1] : 0 })
    });
  }

  pairs.sort((a: AnchorPair, b: AnchorPair): number => a.first - b.first);

  // Monotonluk zorlaması: birinci taraf artarken ikinci taraf geriye giden bir çapa,
  // hizalayıcının bir taşımayı çapa sanmasından gelebilir. Böyle bir çapayı kullanmak
  // kaydırmayı geri zıplatır; ATILIYOR ve bu bir bilgi kaybı değil çünkü taşımalar ayrı
  // eksende raporlanıyor.
  const monotone: AnchorPair[] = [];
  for (const pair of pairs) {
    const previous = monotone[monotone.length - 1];
    if (previous && pair.second < previous.second) continue;
    if (previous && pair.first === previous.first) continue;
    monotone.push(pair);
  }

  const usable: boolean = monotone.length >= 2;

  const interpolate = (value: number, from: "first" | "second"): DocumentPosition | null => {
    if (!usable) return null;
    const key = (p: AnchorPair): number => (from === "first" ? p.first : p.second);
    const other = (p: AnchorPair): number => (from === "first" ? p.second : p.first);

    // Sıralama `first`'e göre; `second`'dan sorgulanırken de monoton olduğu için ikili
    // arama geçerli.
    let low: number = 0;
    let high: number = monotone.length - 1;
    if (value <= key(monotone[0])) {
      low = 0;
      high = 1;
    } else if (value >= key(monotone[high])) {
      low = high - 1;
    } else {
      while (high - low > 1) {
        const mid: number = Math.floor((low + high) / 2);
        if (key(monotone[mid]) <= value) low = mid;
        else high = mid;
      }
      high = low + 1;
    }

    const a = monotone[low];
    const b = monotone[high];
    const span: number = key(b) - key(a);
    // Aynı anahtara düşen iki çapa: oran tanımsız, yakın olanı kullan.
    const ratio: number = span === 0 ? 0 : (value - key(a)) / span;
    return toPosition(other(a) + ratio * (other(b) - other(a)));
  };

  return {
    usable,
    count: monotone.length,
    firstToSecond: (position: DocumentPosition): DocumentPosition | null => interpolate(scalar(position), "first"),
    secondToFirst: (position: DocumentPosition): DocumentPosition | null => interpolate(scalar(position), "second")
  };
}
